"use client";

import { useRef, useEffect } from "react";
import { FilterSelect, type FilterOption } from "@/components/forms/filter-select";
import { readContactReachAction } from "../customer-file/read-contact-reach-action";

/** The searchable list of `ContactPicker`: when a contact is picked, its details are fetched and put into the empty fields of the form around it (see `fillForm`); the fields named in `replace` are overwritten even when not empty. */
export function ContactPickerSelect({ id, options, defaultValue, searchLabel, emptyLabel, replace }: { id: string; options: FilterOption[]; defaultValue?: string; searchLabel: string; emptyLabel: string; replace?: readonly string[] }) {
  const box = useRef<HTMLDivElement>(null);
  const previous = useRef<Record<string, string>>({});
  const pick = async (contactId: string) => {
    const form = box.current?.closest("form");
    if (!form || !contactId) return;
    previous.current = fillForm(form, await readContactReachAction(contactId), previous.current, replace);
  };
  return <div ref={box}><FilterSelect id={id} name="personContactId" options={options} defaultValue={defaultValue ?? ""} searchLabel={searchLabel} emptyLabel={emptyLabel} onChange={pick} /></div>;
}

/** The "copy from" list of a regulated contact form: when a place is chosen, its details (`read`) are put into the empty fields of the form, the same way a picked contact is (see `fillForm`). What the person typed is never overwritten; what a previous choice put there is replaced. */
export function CopyFromSelect({ id, name, options, searchLabel, emptyLabel, read }: { id: string; name: string; options: FilterOption[]; searchLabel: string; emptyLabel: string; read?: (key: string) => Promise<Record<string, string>> }) {
  const box = useRef<HTMLDivElement>(null);
  const previous = useRef<Record<string, string>>({});
  const pick = async (key: string) => {
    const form = box.current?.closest("form");
    if (!form || !key || !read) return;
    previous.current = fillForm(form, await read(key), previous.current);
  };
  return <div ref={box}><FilterSelect id={id} name={name} options={options} defaultValue="" searchLabel={searchLabel} emptyLabel={emptyLabel} onChange={pick} /></div>;
}

/** For a field that keeps its value in its own state: calls `apply` with the value picked for `name` when its form receives the contact-fill event (see `fillForm`). Put the returned ref on the field's hidden input, which tells the hook which form to listen to. */
export function useContactFill(name: string, apply: (value: string) => void) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const form = ref.current?.form;
    if (!form) return;
    const listen = (event: Event) => { const value = (event as CustomEvent<Record<string, string>>).detail[name]; if (value !== undefined) apply(value); };
    form.addEventListener(CONTACT_FILL_EVENT, listen);
    return () => form.removeEventListener(CONTACT_FILL_EVENT, listen);
  }, [name, apply]);
  return ref;
}

/** The name of the event a form receives when a contact was picked: its `detail` holds the values by field name, for the fields that show their value from their own state (the phone field, the drop-down lists). */
export const CONTACT_FILL_EVENT = "contact-fill";

type Field = HTMLInputElement | HTMLTextAreaElement;

function setValue(field: Field, value: string) {
  const prototype = field instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, "value")?.set?.call(field, value);
  field.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Puts the values of a picked contact (by field name) into a form: a field is filled when it is empty or still holds what the last pick put there (`previous`), never over what was typed, except the fields named in `replace` (fields that are never empty, such as a language that starts from a default). Plain fields are set directly; fields with their own state get the contact-fill event. With `clearBlank`, a blank value for a field in `replace` empties it. Returns what was put, to pass as `previous` next time. */
export function fillForm(form: HTMLFormElement, values: Record<string, string>, previous: Record<string, string>, replace: readonly string[] = [], clearBlank = false): Record<string, string> {
  const put: Record<string, string> = {};
  const owned: Record<string, string> = {};
  for (const [name, value] of Object.entries(values)) {
    const field = form.elements.namedItem(name);
    if ((!value && !(clearBlank && replace.includes(name))) || !(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
    if (field.value !== "" && field.value !== previous[name] && !replace.includes(name)) continue;
    put[name] = value;
    if (field.type === "hidden") owned[name] = value;
    else setValue(field, value);
  }
  form.dispatchEvent(new CustomEvent(CONTACT_FILL_EVENT, { detail: owned }));
  return put;
}
